import { T } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';

import { ContactSupport } from '#modules/ui/ContactSupport';
import { CtaButton } from '#modules/ui/CtaButton';
import { Screen } from '#modules/ui/Screen';

import type { AssessmentPendingStateApi } from '../../../hooks/useAssessmentPendingState';
import { useShowContactSupport } from '../../../hooks/useShowContactSupport';
import type { ActiveQuestion } from '../../../utils/resolveQuestionChoices';

import { QuestionField } from './QuestionField';

export type SingleQuestionScreenProps = {
  question: ActiveQuestion;
  pending: AssessmentPendingStateApi;
  isSubmitting: boolean;
  testIDPrefix?: string;
  /** Validates this question and, if it passes, commits + advances. */
  onContinue: () => void;
};

/**
 * One question per screen — the historical assessment layout: the question's
 * own title is the `ScreenHeading` and Continue advances to the next question
 * in the group.
 */
export function SingleQuestionScreen({
  question,
  pending,
  isSubmitting,
  testIDPrefix,
  onContinue,
}: SingleQuestionScreenProps) {
  const showContactSupport = useShowContactSupport();
  const isRepeatingList = question.questionType === 'repeatingList';

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <VStack space='md'>
          {/* Pinned above Continue rather than trailing the last row: the list
              grows without bound, so an inline button walks off the bottom of
              the screen exactly as it becomes most useful. Sharing the footer
              with Continue also reads the two as one control pair on a screen
              whose whole job is "add rows, then continue". */}
          {isRepeatingList ? (
            <CtaButton
              type='secondary'
              isDisabled={isSubmitting}
              onPress={() => pending.addRow(question)}
              testID={testIDPrefix ? `${testIDPrefix}-add-row` : undefined}
            >
              <T _str='+ Add another' />
            </CtaButton>
          ) : null}
          <CtaButton
            isBusy={isSubmitting}
            isDisabled={isSubmitting}
            onPress={onContinue}
            testID={testIDPrefix ? `${testIDPrefix}-continue` : undefined}
          >
            <T _str='Continue' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='2xl'>
        <QuestionField
          question={question}
          headingVariant='screen'
          testIDPrefix={testIDPrefix}
          isSubmitting={isSubmitting}
          state={pending.fieldState(question)}
          handlers={pending.handlers(question)}
        />

        {showContactSupport ? <ContactSupport showPhone /> : null}
      </VStack>
    </Screen>
  );
}
